package fr.manyverse.app.presentation.components.modules.combat

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.Minimize
import androidx.compose.material.icons.filled.TipsAndUpdates
import androidx.compose.material3.Button
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import fr.manyverse.app.domain.model.ActiveCombat
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.CombatParticipantType
import fr.manyverse.app.presentation.components.common_tabs.MessagesBanner
import fr.manyverse.app.presentation.components.player_screen.character.CharacterSheetReadOnly
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.combat_adversary_label
import manyverse.shared.generated.resources.combat_bubble_double_tap_hint
import manyverse.shared.generated.resources.combat_end_turn
import manyverse.shared.generated.resources.combat_its_turn
import manyverse.shared.generated.resources.combat_minimize
import manyverse.shared.generated.resources.combat_move_down
import manyverse.shared.generated.resources.combat_move_up
import manyverse.shared.generated.resources.combat_order_section
import manyverse.shared.generated.resources.combat_remove_participant
import manyverse.shared.generated.resources.combat_stop
import org.jetbrains.compose.resources.stringResource

/**
 * Overlay plein écran du combat côté MJ (image « C'est son tour »).
 * Liste des participants à gauche (actif surligné), détail à droite avec caractéristiques,
 * bouton « Terminer le tour », réorganisation / suppression, réduction et arrêt.
 */
@Composable
fun CombatGmOverlay(
    isVisible: Boolean,
    combat: ActiveCombat,
    characters: List<Character>,
    onNextTurn: () -> Unit,
    onMoveUp: (Int) -> Unit,
    onMoveDown: (Int) -> Unit,
    onRemove: (String) -> Unit,
    onMinimize: () -> Unit,
    onStop: () -> Unit,
    modifier: Modifier = Modifier
) {
    if (!isVisible) return

    NavigationBackHandler(
        state = rememberNavigationEventState(NavigationEventInfo.None),
        onBackCompleted = {}
    )

    // Le Dialog prenait le focus et fermait ainsi le clavier : sans ça, un clavier ouvert
    // (chat, notes) resterait par-dessus le combat
    val focusManager = LocalFocusManager.current
    LaunchedEffect(Unit) { focusManager.clearFocus() }

    // Surface plein écran dans l'arbre de l'écran, et non un Dialog : l'ordre d'affichage avec les
    // autres overlays suit l'ordre de déclaration dans GameMasterScreen. Le Surface bloque aussi
    // les touches vers l'écran dessous.
    Surface(
        modifier = modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                // Le Dialog bornait son contenu à la zone sûre : hors Dialog, il faut éviter aussi
                // la barre de navigation, l'indicateur d'accueil et l'encoche en paysage
                .safeDrawingPadding()
                .padding(16.dp)
        ) {
            // Barre de titre + boutons réduire / arrêter
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    text = stringResource(Res.string.combat_order_section),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                FilledIconButton(
                    onClick = onMinimize,
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.9f)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Minimize,
                        contentDescription = stringResource(Res.string.combat_minimize),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                FilledIconButton(
                    onClick = onStop,
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.9f)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = stringResource(Res.string.combat_stop),
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }

            Spacer(Modifier.height(16.dp))

            MessagesBanner(
                text = stringResource(Res.string.combat_bubble_double_tap_hint),
                icon = Icons.Default.TipsAndUpdates
            )

            Spacer(Modifier.height(16.dp))

            WindowSizeContainer(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
            ) { windowWidth ->
                // Liste des participants (ordre du combat)
                val orderList: @Composable (Modifier) -> Unit = { listModifier ->
                    Column(
                        modifier = listModifier.verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        combat.participants.forEachIndexed { index, participant ->
                            CombatParticipantRow(
                                order = index + 1,
                                participant = participant,
                                characters = characters,
                                isActive = index == combat.activeIndex,
                                canMoveUp = index > 0,
                                canMoveDown = index < combat.participants.lastIndex,
                                onMoveUp = { onMoveUp(index) },
                                onMoveDown = { onMoveDown(index) },
                                onRemove = { onRemove(participant.id) }
                            )
                        }
                    }
                }

                // Détail du participant actif
                val detail: @Composable (Modifier) -> Unit = { detailModifier ->
                    Surface(
                        shape = RoundedCornerShape(20.dp),
                        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f),
                        modifier = detailModifier
                    ) {
                        ActiveParticipantDetail(
                            participant = combat.activeParticipant,
                            characters = characters,
                            onNextTurn = onNextTurn
                        )
                    }
                }

                if (windowWidth == WindowWidth.COMPACT) {
                    // En portrait, le détail s'affiche sous l'ordre du combat.
                    Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        orderList(Modifier.fillMaxWidth().weight(1f))
                        detail(Modifier.fillMaxWidth().weight(1f))
                    }
                } else {
                    Row(
                        modifier = Modifier.fillMaxSize(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        orderList(Modifier.weight(1f).fillMaxHeight())
                        detail(Modifier.weight(1f).fillMaxHeight())
                    }
                }
            }
        }
    }
}

@Composable
private fun CombatParticipantRow(
    order: Int,
    participant: CombatParticipant,
    characters: List<Character>,
    isActive: Boolean,
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit,
    onRemove: () -> Unit
) {
    val containerColor = if (isActive) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)
    else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
    val borderColor = if (isActive) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(containerColor, RoundedCornerShape(14.dp))
            .border(if (isActive) 2.dp else 1.dp, borderColor, RoundedCornerShape(14.dp))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        OrderBadge(order = order, isActive = isActive)
        CombatParticipantAvatar(participant = participant, characters = characters, size = 44.dp)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = participant.name,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            participant.subtitle()?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            IconButton(onClick = onMoveUp, enabled = canMoveUp, modifier = Modifier.size(32.dp)) {
                Icon(Icons.Default.KeyboardArrowUp, contentDescription = stringResource(Res.string.combat_move_up))
            }
            IconButton(onClick = onMoveDown, enabled = canMoveDown, modifier = Modifier.size(32.dp)) {
                Icon(Icons.Default.KeyboardArrowDown, contentDescription = stringResource(Res.string.combat_move_down))
            }
        }
        IconButton(onClick = onRemove) {
            Icon(
                Icons.Default.Delete,
                contentDescription = stringResource(Res.string.combat_remove_participant),
                tint = MaterialTheme.colorScheme.error
            )
        }
    }
}

@Composable
private fun ActiveParticipantDetail(
    participant: CombatParticipant?,
    characters: List<Character>,
    onNextTurn: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = stringResource(Res.string.combat_its_turn).uppercase(),
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary
            )
            Spacer(Modifier.height(20.dp))

            if (participant != null) {
                CombatParticipantAvatar(participant = participant, characters = characters, size = 120.dp)
                Spacer(Modifier.height(16.dp))
                Text(
                    text = participant.name,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold
                )

                participant.sheet(characters)?.let { sheet ->
                    Spacer(Modifier.height(24.dp))
                    CharacterSheetReadOnly(
                        sheet = sheet,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }

        Spacer(Modifier.height(16.dp))

        Button(
            onClick = onNextTurn,
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .navigationBarsPadding()
        ) {
            Text(
                text = stringResource(Res.string.combat_end_turn),
                style = MaterialTheme.typography.titleMedium
            )
            Spacer(Modifier.width(8.dp))
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
        }
    }
}

@Composable
private fun CombatParticipant.subtitle(): String? = when (type) {
    CombatParticipantType.ADVERSARY -> stringResource(Res.string.combat_adversary_label)
    CombatParticipantType.CHARACTER -> null
}

private fun CombatParticipant.sheet(characters: List<Character>): CharacterSheet? {
    if (type != CombatParticipantType.CHARACTER) return null
    val character = characters.firstOrNull { it.id == characterId || it.userId == userId }
    return character?.sheet
}
